import React, { useState } from 'react';
import type { ApiClient } from '../api/client';

type Source = {
  grant_id: string;
  source_kind: string;
  pool_name: string | null;
  model: string;
  sharing_mode: string;
  expires_at: string;
  budget?: unknown;
};

export function FenceSourceBudget({ budget }: { budget: unknown }) {
  const caps = budget && typeof budget === 'object' && !Array.isArray(budget)
    ? budget as Record<string, unknown> : null;
  const valid = caps && Object.keys(caps).length > 0
    && Object.entries(caps).every(([key, value]) =>
      ['max_requests', 'max_tokens'].includes(key)
      && typeof value === 'number' && Number.isSafeInteger(value) && value >= 0);
  return <p>{valid
    ? `Configured grant caps: requests ${caps.max_requests ?? 'not specified'} · tokens ${caps.max_tokens ?? 'not specified'}.`
    : 'Configured grant caps are unavailable here.'} Remaining usage is unavailable here; these are not remaining balances.</p>;
}

export function fenceSourceLabel(source: Pick<Source, 'source_kind' | 'pool_name' | 'model'>): string {
  return source.source_kind === 'credential_pool'
    ? `pools/${source.pool_name ?? '(unnamed pool)'}/${source.model}`
    : source.model;
}

export function fenceSourceImportMessage(executionReady: boolean): string {
  return executionReady
    ? 'Private model profile saved. Select it in a conversation; each run still checks grant access, budget and executor availability.'
    : 'Private profile saved. This server does not report subscription execution support yet.';
}

export default function FenceLinkedSources({ apiClient, onImported }: {
  apiClient: ApiClient;
  onImported: () => Promise<void>;
}) {
  const [sources, setSources] = useState<Source[] | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [imported, setImported] = useState<Set<string>>(new Set());
  const [query, setQuery] = useState('');
  async function load() {
    setBusy(true);
    setError('');
    try {
      const result = await apiClient.get<{ items: Source[]; truncated: boolean }>('/api/me/ai-fence-sources');
      setSources(result.items);
      setTruncated(result.truncated);
    } catch {
      setSources(null);
      setError('Could not load linked sources. Check your existing AI Fence account connection and try again.');
    } finally { setBusy(false); }
  }
  async function save(source: Source) {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await apiClient.post<{ execution_ready: boolean }>('/api/me/ai-fence-sources/import', { grant_id: source.grant_id });
      setImported(previous => new Set([...previous, source.grant_id]));
      setMessage(fenceSourceImportMessage(result.execution_ready));
      await onImported();
    } catch {
      setError('Could not save this source. Refresh to check its current grant and availability.');
    } finally { setBusy(false); }
  }
  return <section aria-label="Linked AI Fence sources" style={{ minWidth: 0 }}>
    <h3>Your linked sources</h3>
    <p>Uses your existing AI Fence account connection and explicit grants. Pool access is not global access, and listed grants do not guarantee remaining provider quota.</p>
    <p>Trusted subscription pool profiles can be used in conversations. Each run still requires grant access, budget and an eligible, available executor. Usage-only sharing is not supported here yet.</p>
    <p>Subscription token budgets apply between requests, not as hard per-response limits. Already-running requests may exceed the remaining budget. Request-count caps remain strict; provider quota-window enforcement is still being implemented.</p>
    <button type="button" className="secondary" disabled={busy} onClick={() => void load()}>
      {busy ? 'Working…' : 'Refresh linked sources'}
    </button>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {sources && <>
      <input aria-label="Search linked sources" placeholder="Search pool or model" value={query}
        onChange={event => setQuery(event.target.value)} style={{ width: '100%', boxSizing: 'border-box', marginTop: 12 }} />
      {sources.length === 0 && <p>No active grants for this linked account. Manage consent and sharing in AI Fence.</p>}
      {sources.filter(source => fenceSourceLabel(source).toLowerCase().includes(query.trim().toLowerCase())).map(source => {
        const supported = source.source_kind === 'credential_pool' && source.sharing_mode === 'trusted';
        const expired = !Number.isFinite(Date.parse(source.expires_at)) || Date.parse(source.expires_at) <= Date.now();
        return <div key={source.grant_id} style={{ marginTop: 12, overflowWrap: 'anywhere' }}>
          <strong>{fenceSourceLabel(source)}</strong>
          <p>{source.sharing_mode === 'trusted' ? 'Trusted credential sharing' : source.sharing_mode} · Expires {new Date(source.expires_at).toLocaleString()}</p>
          <FenceSourceBudget budget={source.budget} />
          <button type="button" disabled={busy || !supported || expired || imported.has(source.grant_id)} onClick={() => void save(source)}>
            {imported.has(source.grant_id) ? 'Saved' : 'Save private profile'}
          </button>
          {!supported && <p>This source mode is not supported by conversation import yet.</p>}
          {expired && <p>This grant has expired.</p>}
        </div>;
      })}
      {truncated && <p>Only the first 500 grants are shown; search filters this loaded subset.</p>}
    </>}
  </section>;
}
